import { Seo } from "../components/Seo";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GoogleButton } from "../components/GoogleButton";
import { OtpVerify } from "../components/OtpVerify";

export default function Register() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("form"); // form | verify
  const [devMode, setDevMode] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setDevMode(!!data.dev_mode);
      setStep("verify");
      toast.success("Verification code sent to your email.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onVerified = (data) => {
    setSession(data.access_token, data.user);
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <Seo path="/register" title="Create your free account" description="Create a free PNR Decode account and start turning raw Sabre & Amadeus PNRs into beautiful, branded flight itineraries in seconds." />
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-14 w-auto object-contain" />
        </div>

        {step === "verify" ? (
          <>
            <OtpVerify email={email.toLowerCase()} devMode={devMode} onVerified={onVerified} />
            <button data-testid="otp-back" onClick={() => setStep("form")} className="mt-6 text-sm text-slate-500 hover:text-slate-800">
              ← Use a different email
            </button>
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">Create account</h1>
            <p className="text-sm text-slate-500 mb-6">Sign up to save your work and access tools.</p>

            <GoogleButton label="Sign up with Google" />
            <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
              <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-slate-700">Name</Label>
                <Input data-testid="register-name" id="name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="Jane Agent" />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input data-testid="register-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input data-testid="register-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" placeholder="At least 6 characters" />
              </div>
              {error && <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
              <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </Button>
            </form>

            <div className="mt-4 text-sm text-slate-600">
              Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
